/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { ForgotPasswordSchema, type ForgotPasswordInput } from '@therapy/shared-validators';
import { ArrowLeft, MailCheck, Send } from 'lucide-react';
import { useCallback, useState, type SyntheticEvent, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FormField } from '../../components/form/form-field.js';
import { apiClient } from '../../lib/api/client.js';

/**
 * Forgot-password request screen. Posts the email to `POST /auth/forgot`, which
 * always returns success (no email enumeration). On success we show the same
 * generic confirmation whether or not the account exists; the reset link is
 * emailed via the mail adapter.
 */
export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(ForgotPasswordSchema) });

  const submit = useCallback(async (values: ForgotPasswordInput): Promise<void> => {
    // An email belongs to exactly one surface (customer or staff). Fire both —
    // each is intentionally non-revealing — and always show the same generic
    // confirmation so an attacker can't probe which emails are registered.
    await Promise.allSettled([
      apiClient.post('/auth/forgot', values),
      apiClient.post('/auth/staff/forgot', values),
    ]);
    setDone(true);
  }, []);

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <span className="bg-brand-600 mb-5 flex h-12 w-12 items-center justify-center rounded-xl text-white">
          <MailCheck className="h-6 w-6" aria-hidden="true" />
        </span>

        {done ? (
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t('forgot.sentTitle')}
            </h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {t('forgot.sentBody')}
            </p>
            <Link
              to="/"
              className="text-brand-600 hover:text-brand-700 mt-6 inline-flex items-center gap-2 text-sm font-medium"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t('forgot.backToLogin')}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t('forgot.title')}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('forgot.subtitle')}
            </p>
            <form className="mt-6 space-y-5" onSubmit={onFormSubmit} noValidate>
              <FormField
                id="forgot-email"
                type="email"
                label={t('login.email')}
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
                error={errors.email?.message}
                registration={register('email')}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="focus-ring bg-brand-600 hover:bg-brand-700 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {t('forgot.submit')}
              </button>
            </form>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t('forgot.backToLogin')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
